import React, { Suspense } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/common/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import LoadingSpinner from './components/common/LoadingSpinner';

// Auth pages - these can stay synchronous as they're critical for app initialization
import Login from './pages/auth/login';
import Register from './pages/auth/register';
import ResetPassword from './pages/auth/reset-password';
import { Logger } from './core/utils/logger';

// Loading component
const SuspenseLoading: React.FC<{ componentName?: string }> = ({ componentName = 'Unknown' }) => {
  return (
    <div
      className="flex justify-center items-center h-64"
      data-loading="true"
      data-component={componentName}
    >
      <div className="text-center">
        <LoadingSpinner />
        <p className="text-gray-500 text-sm mt-2">Loading {componentName}...</p>
      </div>
    </div>
  );
};

// Create lazy components
const createLazyComponent = (importFn: () => Promise<any>, componentName: string) => {
  return React.lazy(() => {
    return importFn().catch(err => {
      // Check if this is a chunk loading error
      if (
        err.message?.includes('Loading chunk') ||
        err.message?.includes('ChunkLoadError') ||
        err.message?.includes('Failed to fetch dynamically imported module')
      ) {
        Logger.warn(`Chunk loading failed for ${componentName}, please refresh manually`);
        // DO NOT auto-reload - was causing infinite reload loops

        return Promise.resolve({
          default: () => (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="text-red-500 mb-4">⚠️</div>
                <p className="text-gray-600 mb-2">Failed to load {componentName}</p>
                <p className="text-sm text-gray-500">Please refresh the page manually</p>
                {/* AUTO-RELOAD DISABLED - was causing infinite loops */}
                {/* Manual refresh button kept for user control */}
                <button
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => {
                    // window.location.reload(); // DISABLED
                    alert(
                      'Please refresh the page manually using your browser refresh button (F5 or Cmd+R)'
                    );
                  }}
                >
                  Refresh Instructions
                </button>
              </div>
            </div>
          ),
        });
      }

      throw err;
    });
  });
};

// Feature pages
const Dashboard = createLazyComponent(() => import('./pages/dashboard'), 'Dashboard');
const AccountsPage = createLazyComponent(() => import('./pages/accounts'), 'AccountsPage');
const OrdersPage = createLazyComponent(() => import('./pages/orders/OrdersPage'), 'OrdersPage');
const TasksPage = createLazyComponent(() => import('./pages/tasks'), 'TasksPage');
const ISSPage = createLazyComponent(() => import('./pages/iss'), 'ISSPage');
const ISSTemplatePage = createLazyComponent(
  () => import('./pages/iss/template'),
  'ISSTemplatePage'
);
const UsersPage = createLazyComponent(() => import('./pages/users'), 'UsersPage');
const RostersPage = createLazyComponent(() => import('./pages/rosters'), 'RostersPage');
const JobsPage = createLazyComponent(() => import('./pages/jobs'), 'JobsPage');
const SettingsPage = createLazyComponent(() => import('./pages/settings'), 'SettingsPage');
const LogoPage = createLazyComponent(() => import('./pages/logo'), 'LogoPage');
const BadgeDemo = createLazyComponent(() => import('./pages/test/BadgeDemo'), 'BadgeDemo');
const AcknowledgementPage = createLazyComponent(
  () => import('./pages/acknowledgement'),
  'AcknowledgementPage'
);
const OperationLogsPage = createLazyComponent(
  () => import('./pages/operation-logs/OperationLogsPage'),
  'OperationLogsPage'
);

// Simple wrapper for lazy routes
const LazyRoute: React.FC<{ children: React.ReactNode; componentName?: string }> = ({
  children,
  componentName = 'LazyRoute',
}) => {
  return (
    <Suspense fallback={<SuspenseLoading componentName={componentName} />}>{children}</Suspense>
  );
};

const Routes: React.FC = () => {
  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/register" element={<Register />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route
        path="/logo"
        element={
          <LazyRoute componentName="LogoPage">
            <LogoPage />
          </LazyRoute>
        }
      />
      <Route
        path="/badge-demo"
        element={
          <LazyRoute componentName="BadgeDemo">
            <BadgeDemo />
          </LazyRoute>
        }
      />

      {/* Protected routes for Manager */}
      <Route
        path="/manager/*"
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <AppLayout>
              <RouterRoutes>
                <Route
                  path="dashboard"
                  element={
                    <LazyRoute componentName="Manager-Dashboard">
                      <Dashboard />
                    </LazyRoute>
                  }
                />
                <Route
                  path="accounts"
                  element={
                    <LazyRoute componentName="Manager-AccountsPage">
                      <AccountsPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="tasks"
                  element={
                    <LazyRoute componentName="Manager-TasksPage">
                      <TasksPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <LazyRoute componentName="Manager-OrdersPage">
                      <OrdersPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="acknowledgement"
                  element={
                    <LazyRoute componentName="Manager-AcknowledgementPage">
                      <AcknowledgementPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="iss"
                  element={
                    <LazyRoute componentName="Manager-ISSPage">
                      <ISSPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="iss/template/:taskId"
                  element={
                    <LazyRoute componentName="Manager-ISSTemplatePage">
                      <ISSTemplatePage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="jobs"
                  element={
                    <LazyRoute componentName="Manager-JobsPage">
                      <JobsPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <LazyRoute componentName="Manager-UsersPage">
                      <UsersPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="roster"
                  element={
                    <LazyRoute componentName="Manager-RostersPage">
                      <RostersPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <LazyRoute componentName="Manager-SettingsPage">
                      <SettingsPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="operation-logs"
                  element={
                    <LazyRoute componentName="Manager-OperationLogsPage">
                      <OperationLogsPage />
                    </LazyRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/manager/accounts" replace />} />
              </RouterRoutes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected routes for Worker */}
      <Route
        path="/worker/*"
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <AppLayout>
              <RouterRoutes>
                <Route
                  path="accounts"
                  element={
                    <LazyRoute componentName="Worker-AccountsPage">
                      <AccountsPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="tasks"
                  element={
                    <LazyRoute componentName="Worker-TasksPage">
                      <TasksPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="iss"
                  element={
                    <LazyRoute componentName="Worker-ISSPage">
                      <ISSPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="iss/template/:taskId"
                  element={
                    <LazyRoute componentName="Worker-ISSTemplatePage">
                      <ISSTemplatePage />
                    </LazyRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/worker/accounts" replace />} />
              </RouterRoutes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected routes for Seller - Only Accounts and Orders pages */}
      <Route
        path="/seller/*"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <AppLayout>
              <RouterRoutes>
                <Route
                  path="accounts"
                  element={
                    <LazyRoute componentName="Seller-AccountsPage">
                      <AccountsPage />
                    </LazyRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <LazyRoute componentName="Seller-OrdersPage">
                      <OrdersPage />
                    </LazyRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/seller/accounts" replace />} />
              </RouterRoutes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected routes for Driver - Only Orders page */}
      <Route
        path="/driver/*"
        element={
          <ProtectedRoute allowedRoles={['driver']}>
            <AppLayout>
              <RouterRoutes>
                <Route
                  path="orders"
                  element={
                    <LazyRoute componentName="Driver-OrdersPage">
                      <OrdersPage />
                    </LazyRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/driver/orders" replace />} />
              </RouterRoutes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </RouterRoutes>
  );
};

export default Routes;